"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";

import React, { useMemo } from "react";
import { cn, dayjs } from "utils";
import Translations from "@translate/components/Portfolio/AccountExpiredToast/AccountExpiredToast";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

export interface AccountExpiredToastProps {
  /** 自定义类名 */
  className?: string;
  /** 点击 Renewal 按钮的回调 */
  onRenewalClick?: () => void;
  /** 最后同步时间（时间戳） */
  lastSyncedTime?: number;
  loading?: boolean;
}

/**
 * 账户授权过期 Toast 组件
 * 当导入的投资组合账户状态为 DISABLED 时显示
 */
export const AccountExpiredToast: React.FC<AccountExpiredToastProps> = ({
  className,
  onRenewalClick,
  lastSyncedTime,
  loading,
}) => {
  // 格式化最后同步时间
  const formattedTime = useMemo(() => {
    if (!lastSyncedTime) {
      return "--";
    }
    return dayjs(lastSyncedTime).format("MMM DD, HH:mm");
  }, [lastSyncedTime]);

  // 构建完整文案
  const message = useMemo(() => {
    const template = I18n.t(
      "The binding has expired. Last Update time: {time}",
    );
    return template.replace("{time}", formattedTime);
  }, [formattedTime]);
  return (
    <div
      className={cn(
        "flex gap-4 items-center justify-center px-0 py-2 w-full",
        "bg-background_accent2_overlay10 px-4",
        className,
      )}
    >
      {/* 左侧：错误图标和文本 */}
      <div className="flex gap-2 items-center min-w-0">
        {/* 错误图标 */}
        <Icon
          name="error-circle"
          size={16}
          color={ospThemes.colors.content_accent2_primary}
        />
        {/* 提示文本 */}
        <p className="body_medium_s text-content_primary flex-1 min-w-0">
          {message}
        </p>
      </div>
      {/* 右侧：Renewal 按钮 */}
      <button
        aria-label="Account Expired Toast"
        data-openreplay-label="Account Expired Toast Button"
        id="btn-account-expired-toast"
        onClick={onRenewalClick}
        className={cn(
          "button group xs_button emphasis_button min-w-22 button_s flex-shrink-0",
        )}
        disabled={loading}
        data-loading={loading}
      >
        <span className="group-loading:hidden">{I18n.t("Rebind")}</span>
        <Icon
          name="ic_loading2"
          size={20}
          color={ospThemes.colors.content_inverse_primary}
          className={cn("hidden animate-spin group-loading:block")}
        />
      </button>
    </div>
  );
};
